import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

/**
 * Load employee master data from backend (no hardcoded departments/grades/roles).
 */
export function useEmployeeMasters() {
  const [masters, setMasters] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchMasters = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get('/v1/employee-master/masters/all');
      const data = res.data;
      setMasters({
        departments: (data.departments || []).map((d) => ({
          value: d.id,
          label: d.name,
          code: d.code,
        })),
        designations: (data.designations || []).map((d) => ({
          value: d.id,
          label: d.title,
          grade: d.grade,
          department_id: d.department_id,
        })),
        employeeTypes: (data.employee_types || []).map((t) => ({
          value: t.id,
          label: t.name,
          code: t.code,
        })),
        grades: (data.grades || []).map((g) => ({
          value: g.id,
          label: `${g.code} — ${g.name}`,
          code: g.code,
        })),
        roles: (data.roles || [])
          .map((r) => ({
            value: r.id,
            slug: r.name,
            label: r.description || r.name,
            dashboard: r.dashboard_tier || 'user',
            displayOrder: r.display_order ?? 100,
          }))
          .sort((a, b) => a.displayOrder - b.displayOrder),
        shiftGroups: (data.shift_groups || []).map((g) => ({ id: g.id, name: g.name, code: g.code })),
        rosterGroups: (data.roster_groups || []).map((g) => ({ id: g.id, name: g.name, code: g.code })),
        payrollGroups: (data.payroll_groups || []).map((g) => ({ id: g.id, name: g.name, code: g.code })),
        leavePolicies: (data.leave_policies || []).map((g) => ({ id: g.id, name: g.name, code: g.code })),
        attendancePolicies: (data.attendance_policies || []).map((g) => ({ id: g.id, name: g.name, code: g.code })),
        documentTypes: (data.document_types || []).map((t) => ({
          value: t.id,
          id: t.id,
          label: t.name,
          code: t.code,
          mandatory: t.is_mandatory,
          document_group: t.document_group,
          source: t.source,
        })),
        branches: (data.branches || []).map((b) => ({
          value: b.id,
          id: b.id,
          label: b.name,
          name: b.name,
          code: b.code,
        })),
        locations: (data.locations || []).map((l) => ({
          value: l.id,
          id: l.id,
          label: l.name,
          branch_id: l.branch_id,
          department_id: l.department_id,
        })),
        officeLocations: (data.office_locations || []).map((o) => ({
          value: o.id,
          id: o.id,
          label: o.name,
        })),
        shifts: (data.shifts || []).map((s) => ({
          value: s.id,
          id: s.id,
          label: s.name,
          code: s.code,
        })),
        scales: (data.scales || []).map((s) => {
          const codeOrName = (s.code && s.name && s.name.toLowerCase().includes(s.code.toLowerCase())) ? s.code : (s.code || s.name);
          const minB = s.min_basic != null ? Math.round(s.min_basic) : null;
          const maxB = s.max_basic != null ? Math.round(s.max_basic) : null;
          const label = (minB !== null || maxB !== null) ? `${codeOrName} ( ${minB ?? 0} - ${maxB ?? 0} )` : codeOrName;
          return {
            value: s.id || s.name || s.code,
            id: s.id,
            code: s.code,
            name: s.name,
            min_basic: s.min_basic,
            max_basic: s.max_basic,
            label: label,
            pay_level: s.pay_level,
          };
        }),
        headCodes: (data.head_codes || []).map((h) => ({
          value: h.head_code,
          label: h.head_code,
        })),
      });
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load master data');
      const fallback = await axios.get('/v1/departments').catch(() => null);
      if (fallback?.data) {
        setMasters({
          departments: fallback.data.map((d) => ({ value: d.id, label: d.name })),
          designations: [],
          employeeTypes: [],
          grades: [],
          roles: [],
          documentTypes: [],
          branches: [],
        });
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const searchManagers = useCallback(async (q = '') => {
    try {
      const res = await axios.get('/v1/employee-master/managers', { params: { q, limit: 30 } });
      return res.data;
    } catch {
      return [];
    }
  }, []);

  const fetchDesignations = useCallback(async (departmentId = null, designationType = null) => {
    try {
      const params = {};
      if (departmentId) params.department_id = departmentId;
      if (designationType) params.designation_type = designationType;
      const res = await axios.get('/v1/designations/designation', { params });
      console.log("fetchDesignations - API response:", res.data);
      const mapped = (res.data || []).map((d) => ({
        value: d.id,
        label: d.title,
        grade: d.grade ?? d.grade_name,
        grade_name: d.grade_name,
        department_id: d.department_id,
      }));
      console.log("fetchDesignations - mapped data:", mapped);
      return mapped;
    } catch {
      return [];
    }
  }, []);

  // For Add Employee (Payroll Heads)
  const fetchDepartmentHeads = useCallback(async (departmentId) => {
    if (!departmentId) return [];
    try {
      const res = await axios.get(`/v1/department-heads/department/${departmentId}`);
      const headsList = res.data.heads || [];
      return headsList.map((h) => ({
        value: h.id,
        label: h.head_code,
      }));
    } catch {
      return [];
    }
  }, []);

  // For Promotion (Employee Managers/Heads)
  const fetchManagerHeads = useCallback(async (departmentId) => {
    if (!departmentId) return [];
    try {
      const res = await axios.get(`/v1/employee-master/department/${departmentId}/heads`);
      const mapped = (res.data || []).map((h) => ({
        value: h.id,
        label: `${h.name} (${h.emp_code})${h.designation ? ` - ${h.designation}` : ''}`,
      }));
      return mapped;
    } catch {
      return [];
    }
  }, []);

  const fetchHeads = useCallback(async () => {
    try {
      const res = await axios.get('/v1/employee-master/masters/heads');
      return res.data.map((h) => ({
        value: h.head_code,
        label: h.head_code,
        department_id: h.department_id,
      }));
    } catch {
      return [];
    }
  }, []);

  useEffect(() => {
    fetchMasters();
  }, [fetchMasters]);

  return { masters, loading, error, refetch: fetchMasters, searchManagers, fetchDesignations, fetchDepartmentHeads, fetchManagerHeads, fetchHeads };
}

export default useEmployeeMasters;
